import { useMemo, useState, ChangeEvent, useRef } from 'react';
import { useLegalEntities, travelerPersonalTranslationKeys } from 'obt-common';
import Input from '@spotnana/pixel-react/dist/Input';
import * as Icons from '@spotnana/pixel-react/dist/Icons';
import Box from '@spotnana/pixel-react/dist/Box';
import DropdownList from '@spotnana/pixel-react/dist/DropdownList';
import { css, CSSObject } from 'styled-components';
import { useTranslation } from 'react-i18next';

const { SELECT_ALL_LEGAL_ENTITIES } = travelerPersonalTranslationKeys;
const DISABLED_OPACITY = 0.6;
const inputStyles = css`
  min-height: 56px;
  display: flex;
  align-items: center;
`;

const inputCssStyles = css`
  align-self: center;
  padding-bottom: 0;
`;

export interface ILegalEntitiesSelectProps {
  organizationId: string;
  selectedItemIds: string[];
  setSelectedItemIds: (value: string[]) => void;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
}

const LegalEntitiesSelect = ({
  organizationId,
  selectedItemIds,
  setSelectedItemIds,
  disabled = false,
  label = '',
  placeholder,
}: ILegalEntitiesSelectProps): JSX.Element => {
  const { t: tProfile } = useTranslation('PROFILE');
  const { t: tt } = useTranslation('WEB');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [search, setSearch] = useState('');
  const { legalEntities } = useLegalEntities({ organizationId });

  const options = useMemo(
    () =>
      legalEntities.map((legalEntity) => ({
        id: legalEntity.id.id,
        label: legalEntity.name,
      })),
    [legalEntities],
  );

  const handleSearch = (e: ChangeEvent<HTMLInputElement>): void => {
    setSearch(e.target.value);
  };

  const handleOnClose = (): void => {
    setShowDropdown(false);
    setSearch('');
  };

  return (
    <>
      <Box
        data-testid={`legal-entities-search-input-container${disabled ? '-disabled' : ''}`}
        ref={inputRef}
        onClick={!disabled ? (): void => setShowDropdown((prevShowDropdown) => !prevShowDropdown) : undefined}
        opacity={disabled ? DISABLED_OPACITY : 1}
        style={{ pointerEvents: disabled ? 'none' : undefined }}
      >
        <Input
          label={label}
          containerCssStyles={inputStyles}
          inputCssStyles={label ? undefined : (inputCssStyles as unknown as CSSObject)}
          id="legal-entities-search"
          placeholder={placeholder ?? tt('Click to search and select')}
          variation={label ? 'large' : 'small'}
          iconRight={<Icons.ChevronDown />}
          value={search}
          data-testid="search"
          onChange={handleSearch}
          disabled={disabled}
          readOnly={disabled}
        />
      </Box>
      <DropdownList
        selectAllLabel={tProfile(SELECT_ALL_LEGAL_ENTITIES)}
        isVisible={showDropdown}
        items={options}
        keyProp="id"
        labelProp="label"
        anchorRef={inputRef}
        selectedItemIds={selectedItemIds}
        search={search}
        xOffset={2}
        yOffset={2}
        onSelect={setSelectedItemIds}
        onClose={handleOnClose}
      />
    </>
  );
};

export default LegalEntitiesSelect;
